import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Users,
  Handshake,
  Loader2
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SeoHead from '../components/seo/SeoHead';
import { publicService } from '../services/public.service';

const iconMap = {
  shield: ShieldCheck,
  sparkles: Sparkles,
  users: Users,
  'heart-handshake': Handshake
};

const AboutPage = () => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadAboutPage = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await publicService.getAboutPageData();
        setAboutData(response);
      } catch (err) {
        setError(err.message || 'Failed to load the about page.');
      } finally {
        setLoading(false);
      }
    };

    loadAboutPage();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !aboutData) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="rounded-[2rem] border border-red-100 bg-red-50 px-8 py-10 text-center text-red-700 font-semibold max-w-xl">
            {error || 'Unable to load about page content.'}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 text-brand-black flex flex-col font-sans">
      <SeoHead
        title={`About Go Rides | ${aboutData.heroTitle || 'Premium Mobility in Rwanda'}`}
        description={aboutData.heroDescription || 'Learn about Go Rides, our mission, our values, and how we are building trusted premium mobility experiences in Rwanda.'}
        canonicalPath="/about"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Go Rides',
          url: `${window.location.origin}/about`,
          description: aboutData.heroDescription || 'Premium mobility experiences in Rwanda',
          slogan: 'Your Journey, Your way'
        }}
      />
      <Navbar />

      <main className="flex-1">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 lg:pt-10">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-4 lg:gap-8 items-center">
            <div className="max-w-xl">
              <p className="text-xs font-semibold text-orange-700 mb-3">ABOUT GO RIDES</p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-6">
                {aboutData.heroTitle}
              </h1>
              <p className="text-sm sm:text-base leading-7 text-gray-500 max-w-lg mb-8">
                {aboutData.heroDescription}
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to={aboutData.heroPrimaryCtaUrl || '/browse'}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#FF6B00] px-5 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-[#e65f00] transition-colors"
                >
                  {aboutData.heroPrimaryCtaLabel || 'Explore Our Fleet'}
                </Link>
                <Link
                  to={aboutData.heroSecondaryCtaUrl || '/register'}
                  className="inline-flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-brand-black transition-colors"
                >
                  {aboutData.heroSecondaryCtaLabel || 'Discover'}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-[1.6rem] overflow-hidden bg-[#dfe5ea] shadow-sm">
                <img
                  src={aboutData.heroImageUrl}
                  alt="Go Rides mobility"
                  className="w-full h-[260px] sm:h-[320px] object-cover"
                />
              </div>
              <div className="absolute left-4 bottom-4 rounded-xl bg-[#171717]/90 text-white px-4 py-3">
                <p className="text-xl font-bold tracking-tight">{aboutData.heroStatValue}</p>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gray-400 mt-2">
                  {aboutData.heroStatLabel}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 lg:pt-10">
          <div className="grid lg:grid-cols-2 gap-4 lg:gap-5">
            <div className="rounded-[1.8rem] bg-white  p-5 lg:p-6 shadow-sm">
              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-gray-400 mb-4">
                {aboutData.philosophyEyebrow}
              </p>
              <h2 className="text-2xl lg:text-3xl font-bold tracking-tight mb-5">
                {aboutData.philosophyTitle}
              </h2>
              <p className="text-sm leading-7 text-gray-500">
                {aboutData.philosophyDescription}
              </p>
            </div>
            <div className="rounded-[1.8rem] bg-white  p-5 lg:p-6 shadow-sm">
              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-gray-400 mb-4">
                Our Mission
              </p>
              <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-brand-black mb-5">
                {aboutData.missionTitle}
              </h2>
              <p className="text-sm leading-7 text-gray-500">
                {aboutData.missionDescription}
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 lg:pt-10">
          <h2 className="text-2xl lg:text-3xl font-bold tracking-tight mb-5">
            {aboutData.valuesTitle}
          </h2>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
            {aboutData.values?.map((value) => {
              const Icon = iconMap[value.iconKey] || ShieldCheck;
              return (
                <div key={value.title} className="rounded-[1.6rem] bg-white  p-6 shadow-sm">
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-stone-50 text-brand-black">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-bold mb-3">{value.title}</h3>
                  <p className="text-sm leading-7 text-gray-500">{value.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 lg:pt-10">
          <div className="text-center max-w-3xl mx-auto mb-5">
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight mb-3">
              {aboutData.leadershipTitle}
            </h2>
            <p className="text-base text-gray-500 leading-8">
              {aboutData.leadershipDescription}
            </p>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
            {aboutData.leaders?.map((leader) => (
              <div key={leader.fullName} className="rounded-[1.6rem] bg-white  p-5 shadow-sm text-center">
                <div className="aspect-[4/3] overflow-hidden rounded-[1.25rem] bg-[#dfe5ea] mb-5">
                  <img
                    src={leader.imageUrl}
                    alt={leader.fullName}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-lg font-bold">{leader.fullName}</h3>
                <p className="text-sm text-gray-500 mt-2">{leader.roleTitle}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
          <div className="rounded-[2rem] bg-white  px-6 py-8 lg:px-8 text-center shadow-sm">
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight mb-5">
              {aboutData.ctaTitle}
            </h2>
            <p className="text-sm leading-7 text-gray-500 max-w-2xl mx-auto mb-8">
              {aboutData.ctaDescription}
            </p>
            <Link
              to={aboutData.ctaButtonUrl || '/register'}
              className="inline-flex items-center gap-2 rounded-xl bg-[#FF6B00] px-5 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-[#e65f00] transition-colors"
            >
              {aboutData.ctaButtonLabel || 'Become our partner'}
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
